import {
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import {
  createTeamShareLink,
  listClubShareLinks,
  listTeamShareLinks,
  revokeTeamShareLink,
} from "@/lib/api";
import { unscopedKey } from "@/lib/queryClient";
import { useAuthStore } from "@/stores/auth";
export function useGuestLinks(teamId: string) {
  const viewer = useAuthStore((s) => s.me?.user?.id);
  const host = useAuthStore((s) => s.me?.current_club?.id);
  const client = useQueryClient();
  const key = unscopedKey("team-share-links", { teamId, viewer, host });
  const refresh = () =>
    Promise.all([
      client.invalidateQueries({ queryKey: key }),
      client.invalidateQueries({ queryKey: unscopedKey("club-share-links") }),
    ]);
  const list = useQuery({
    queryKey: key,
    queryFn: () => listTeamShareLinks(teamId),
    enabled: !!viewer && !!teamId,
  });
  const create = useMutation({
    mutationFn: (input: Parameters<typeof createTeamShareLink>[1]) =>
      createTeamShareLink(teamId, input),
    onSuccess: refresh,
  });
  const revoke = useMutation({
    mutationFn: (id: string) => revokeTeamShareLink(teamId, id),
    onSuccess: refresh,
  });
  return { list, create, revoke, identity: `${host}:${viewer}:${teamId}` };
}

/** Load all guest-link metadata for the host club in one request. */
export function useClubGuestLinks(
  teams: readonly { id: string; name: string }[],
) {
  const viewer = useAuthStore((s) => s.me?.user?.id);
  const host = useAuthStore((s) => s.me?.current_club?.id);
  const query = useQuery({
    queryKey: unscopedKey("club-share-links", { viewer, host }),
    queryFn: listClubShareLinks,
    enabled: !!viewer && !!host && teams.length > 0,
  });
  return {
    links: (query.data?.links ?? []).map(({ team_name, ...link }) => ({
      ...link,
      teamName: team_name,
    })),
    loading: query.isLoading,
    error: query.isError,
    retry: query.refetch,
  };
}
